/**
 * Kelas — satu KALI penyelenggaraan sebuah program.
 *
 * Program adalah katalog kurikulumnya dan dipakai berulang; kelas adalah
 * rombongan yang menjalaninya pada rentang waktu tertentu. Yang menempel di
 * kelas adalah hal-hal yang berbeda tiap penyelenggaraan: pesertanya, sesinya,
 * dan apakah ia dibiayai satu perusahaan (inhouse) atau terbuka (publik).
 */
import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon } from "../shared"
import { Btn } from "../shared/components"
import {
  listClasses, createClass, updateClass, listProgramTrainers,
  classDeleteImpact, deleteClass, openJoinCode, closeJoinCode,
} from "../api/programs.api"
import { createSession } from "../api/sessions.api"
import { durasiDariJam, labelDurasi } from "../lib/sessionTime"

const MODE = ["INHOUSE", "PUBLIC"]
const STATUS = ["DRAFT", "ONGOING", "FINISHED"]

// Tanggal dari server datang sebagai ISO penuh; input[type=date] hanya mau
// bagian tanggalnya.
// keISO menyimpan tengah malam lokal (10 Feb WIB = 9 Feb 17:00 UTC), jadi memotong
// string ISO-nya menampilkan hari sebelumnya — dan menyimpan ulang form memundurkan
// tanggalnya sehari lagi. Dibulatkan ke hari UTC terdekat, sama dengan sertifikat.
const keInput = (v) => (v ? new Date(new Date(v).getTime() + 12 * 60 * 60 * 1000).toISOString().slice(0, 10) : "")
const keISO = (v) => (v ? new Date(`${v}T00:00:00`).toISOString() : null)

function labelStatus(s, id) {
  if (!id) return s.charAt(0) + s.slice(1).toLowerCase()
  return { DRAFT: "Draf", ONGOING: "Berjalan", FINISHED: "Selesai" }[s] || s
}

// 52 adalah batas atas yang diterima mesin sesi berulang di server.
function jumlahSesi(start, end, frequency) {
  const hari = Math.round((new Date(end) - new Date(start)) / 86400000)
  const n = frequency === "DAILY" ? hari + 1 : Math.floor(hari / 7) + 1
  return Math.min(52, Math.max(1, n))
}

/** Kode yang ada tapi sudah lewat waktunya sama saja dengan pintu tertutup. */
function kodeAktif(k) {
  return !!k.self_enroll_code
    && (!k.self_enroll_code_expires_at || new Date(k.self_enroll_code_expires_at) > new Date())
}

function tanggalRingkas(v, id) {
  return new Date(v).toLocaleDateString(id ? "id-ID" : "en-GB", {
    day: "numeric", month: "short", year: "numeric",
  })
}

export default function ClassesScreen({ embedded = false }) {
  const { lang, currentProgramId, currentProgram, currentClassId, setCurrentClassId, refreshClasses } = useApp()
  const id = lang === "id"
  const [rows, setRows] = React.useState([])
  const [sibuk, setSibuk] = React.useState(false)
  const [sunting, setSunting] = React.useState(null)
  const [generate, setGenerate] = React.useState(null)
  const [hapus, setHapus] = React.useState(null)
  const [kodeUntuk, setKodeUntuk] = React.useState(null)

  const muat = React.useCallback(() => {
    if (!currentProgramId) { setRows([]); return }
    setSibuk(true)
    listClasses(currentProgramId)
      .then(setRows)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setSibuk(false))
  }, [currentProgramId])

  React.useEffect(() => { muat() }, [muat])

  async function simpan(data) {
    try {
      if (sunting?.id) {
        await updateClass(currentProgramId, sunting.id, data)
        toast.success(id ? "Kelas diperbarui" : "Class updated")
      } else {
        const baru = await createClass(currentProgramId, data)
        toast.success(id ? "Kelas dibuka" : "Class created")
        // Kelas yang baru dibuka langsung dijadikan kelas aktif — itu yang
        // hendak dikerjakan orang setelah membuatnya.
        setCurrentClassId(baru.id)
      }
      setSunting(null)
      muat()
      refreshClasses()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  if (!currentProgramId) {
    return (
      <div className="col gap-3">
        {!embedded && <h1 className="h1">{id ? "Kelas" : "Classes"}</h1>}
        <div className="muted">{id ? "Pilih program dulu." : "Pick a program first."}</div>
      </div>
    )
  }

  return (
    <div className="col gap-3">
      <div className="row between" style={{ alignItems: "flex-start", gap: 12 }}>
        <div>
          {!embedded && <h1 className="h1">{id ? "Kelas" : "Classes"}</h1>}
          <div className="muted small" style={{ maxWidth: 620, lineHeight: 1.6 }}>
            {id
              ? `Tiap kali ${currentProgram?.name || "program ini"} diselenggarakan, buka satu kelas baru. Peserta, sesi, dan kehadiran dihitung per kelas — materi dan tesnya tetap dipakai bersama.`
              : `Open a new class each time ${currentProgram?.name || "this program"} runs. Participants, sessions, and attendance are counted per class — the material and tests stay shared.`}
          </div>
        </div>
        <Btn variant="primary" onClick={() => setSunting({})}>
          {id ? "Buka kelas" : "New class"}
        </Btn>
      </div>

      {sibuk && rows.length === 0 && <div className="muted small">…</div>}

      {!sibuk && rows.length === 0 && (
        <div className="tile tile-pad muted" style={{ lineHeight: 1.6 }}>
          {id
            ? "Belum ada kelas. Peserta yang sudah terdaftar sebelum ada kelas tetap aman — mereka masuk ke kelas penampung saat pemutakhiran."
            : "No classes yet. Participants enrolled before classes existed were moved into a carry-over class."}
        </div>
      )}

      {rows.length > 0 && (
        <div className="tile">
          {rows.map((k) => (
            <div
              key={k.id}
              className="row between gap-2"
              style={{
                padding: "12px 14px",
                alignItems: "center",
                borderBottom: "1px solid var(--border-subtle)",
                background: k.id === currentClassId ? "var(--surface-2)" : "transparent",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="row gap-2" style={{ alignItems: "center" }}>
                  <span style={{ fontWeight: 600 }}>{k.name}</span>
                  <span className="tiny" style={{
                    padding: "2px 7px", borderRadius: 20,
                    background: "var(--surface-2)", color: "var(--text-3)",
                  }}>
                    {k.mode === "INHOUSE" ? "Inhouse" : (id ? "Publik" : "Public")}
                  </span>
                  <span className="tiny muted">{labelStatus(k.status, id)}</span>
                </div>
                <div className="tiny muted" style={{ marginTop: 3 }}>
                  {k.mode === "INHOUSE" && k.client_company ? `${k.client_company} · ` : ""}
                  {k._count?.enrollments ?? 0} {id ? "peserta" : "participants"}
                  {" · "}
                  {k._count?.sessions ?? 0} {id ? "sesi" : "sessions"}
                  {k.start_date ? ` · ${new Date(k.start_date).toLocaleDateString(id ? "id-ID" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                  {k.location ? ` · ${k.location}` : ""}
                </div>
              </div>
              <div className="row gap-2" style={{ flexShrink: 0 }}>
                {/* Tanggal kelas sudah diisi sekali; tombol ini memakainya
                    langsung supaya tanggal yang sama tidak diketik ulang
                    sesi demi sesi. */}
                {/* Pendaftaran mandiri menempel di kelas, bukan program — kode
                    se-program tidak bisa memberi tahu angkatan mana yang
                    dimasuki peserta. Titik hijau supaya admin tahu sekilas
                    kelas mana yang pintunya sedang terbuka. */}
                <Btn
                  variant="secondary"
                  size="sm"
                  onClick={() => setKodeUntuk(k)}
                  title={id ? "Kode pendaftaran mandiri" : "Self-registration code"}
                >
                  {kodeAktif(k) && (
                    <span
                      style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: "var(--success)", display: "inline-block", marginRight: 6,
                      }}
                    />
                  )}
                  {id ? "Kode Kelas" : "Class Code"}
                </Btn>
                <Btn
                  variant="secondary"
                  size="sm"
                  disabled={!k.start_date || !k.end_date}
                  title={
                    !k.start_date || !k.end_date
                      ? (id ? "Isi tanggal mulai & selesai kelas dulu" : "Set the class start and end dates first")
                      : undefined
                  }
                  onClick={() => setGenerate(k)}
                >
                  {id ? "Generate sesi" : "Generate sessions"}
                </Btn>
                {k.id !== currentClassId && (
                  <Btn variant="secondary" size="sm" onClick={() => setCurrentClassId(k.id)}>
                    {id ? "Buka" : "Open"}
                  </Btn>
                )}
                <Btn variant="ghost" size="sm" onClick={() => setSunting(k)}>
                  <Icon name="edit" size={14} />
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  title={id ? "Hapus kelas" : "Delete class"}
                  onClick={() => setHapus(k)}
                >
                  <Icon name="trash" size={14} />
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {sunting && (
        <ModalKelas
          kelas={sunting}
          lang={lang}
          onSimpan={simpan}
          onTutup={() => setSunting(null)}
        />
      )}

      {generate && (
        <ModalGenerateSesi
          kelas={generate}
          programId={currentProgramId}
          lang={lang}
          onSelesai={() => { setGenerate(null); muat() }}
          onTutup={() => setGenerate(null)}
        />
      )}

      {hapus && (
        <ModalHapusKelas
          kelas={hapus}
          programId={currentProgramId}
          lang={lang}
          onSelesai={() => {
            // Kelas yang sedang dibuka ikut lenyap — lepaskan pilihannya
            // supaya layar lain tidak menyaring ke kelas yang tak ada lagi.
            if (hapus.id === currentClassId) setCurrentClassId(null)
            setHapus(null)
            muat()
            refreshClasses()
          }}
          onTutup={() => setHapus(null)}
        />
      )}

      {kodeUntuk && (
        <ModalKodePendaftaran
          kelas={kodeUntuk}
          programId={currentProgramId}
          lang={lang}
          onBerubah={() => { muat(); refreshClasses() }}
          onTutup={() => setKodeUntuk(null)}
        />
      )}
    </div>
  )
}

/**
 * Kode pendaftaran mandiri — jalan masuk peserta yang datanya belum pernah ada
 * di sistem.
 *
 * Menempel di KELAS, bukan program: sebuah program dijalankan berkali-kali, jadi
 * kode se-program tidak bisa memberi tahu angkatan mana yang dimasuki peserta —
 * dan itulah sebabnya jalur ini dulu membuat enrolmen tanpa kelas sama sekali.
 *
 * Kodenya dipampang besar dan monospasi karena kegunaannya memang dibacakan dari
 * papan atau proyektor lalu diketik puluhan orang sekaligus. Sekali salah baca
 * berarti satu tangan terangkat di tengah pelatihan.
 */
function ModalKodePendaftaran({ kelas, programId, lang, onBerubah, onTutup }) {
  const id = lang === "id"
  const [kode, setKode] = React.useState(kelas.self_enroll_code || null)
  const [berlakuSampai, setBerlakuSampai] = React.useState(kelas.self_enroll_code_expires_at || null)
  // Masa berlaku dalam jam — bentuk yang sama dengan parameter `hours` di API.
  // Pelatihan sehari dan pelatihan lima hari jelas butuh angka berbeda, jadi ini
  // ditanyakan, bukan dipatok.
  const [durasi, setDurasi] = React.useState(72)
  const [sibuk, setSibuk] = React.useState(false)

  const PILIHAN_DURASI = [
    { jam: 12, label: id ? "12 jam" : "12 hours" },
    { jam: 24, label: id ? "1 hari" : "1 day" },
    { jam: 72, label: id ? "3 hari" : "3 days" },
    { jam: 168, label: id ? "7 hari" : "7 days" },
  ]

  const kedaluwarsa = berlakuSampai && new Date(berlakuSampai) < new Date()
  const selesai = kelas.status === "FINISHED"

  async function buka() {
    setSibuk(true)
    try {
      const r = await openJoinCode(programId, kelas.id, durasi)
      setKode(r.joinCode)
      setBerlakuSampai(r.expiresAt)
      onBerubah()
      toast.success(id ? "Pendaftaran mandiri dibuka" : "Self-registration opened")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally { setSibuk(false) }
  }

  async function tutup() {
    if (!window.confirm(id
      ? "Tutup pendaftaran mandiri? Peserta baru tidak bisa masuk lagi dengan kode ini."
      : "Close self-registration? New participants can no longer join with this code.")) return
    setSibuk(true)
    try {
      await closeJoinCode(programId, kelas.id)
      setKode(null); setBerlakuSampai(null)
      onBerubah()
      toast.success(id ? "Pendaftaran mandiri ditutup" : "Self-registration closed")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally { setSibuk(false) }
  }

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "Pendaftaran mandiri" : "Self-registration"}</span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body col gap-3">
          <div>
            <div className="eyebrow">{id ? "Kelas" : "Class"}</div>
            <div style={{ fontWeight: 600, marginTop: 2 }}>{kelas.name}</div>
          </div>

          {selesai ? (
            <div className="muted small" style={{ lineHeight: 1.6 }}>
              {id
                ? "Kelas ini sudah selesai, jadi pendaftaran mandiri tidak bisa dibuka. Membuka pintu ke angkatan yang sudah bubar hanya menghasilkan peserta yang terdaftar di ruangan kosong."
                : "This class is finished, so self-registration cannot be opened. Opening the door to a cohort that has already dispersed only produces participants enrolled in an empty room."}
            </div>
          ) : kode && !kedaluwarsa ? (
            <div className="tile tile-pad" style={{ textAlign: "center" }}>
              <div className="mono" style={{ fontSize: 34, fontWeight: 700, letterSpacing: "0.14em" }}>
                {kode}
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                {id ? "Berlaku sampai" : "Valid until"}{" "}
                {new Date(berlakuSampai).toLocaleString(id ? "id-ID" : "en-GB", {
                  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                })}
              </div>
            </div>
          ) : (
            <div className="muted small" style={{ lineHeight: 1.6 }}>
              {kedaluwarsa
                ? (id ? "Kode sebelumnya sudah kedaluwarsa." : "The previous code has expired.")
                : (id
                  ? "Tertutup. Buka bila peserta perlu mendaftarkan dirinya sendiri di ruangan — mereka cukup mengetik nama dan perusahaannya, lalu langsung masuk ke kelas ini."
                  : "Closed. Open it when participants need to register themselves in the room — they only type their name and company, and land directly in this class.")}
            </div>
          )}

          {!selesai && (
            <>
              <label className="field">
                <span className="field-label">{id ? "Berlaku" : "Valid for"}</span>
                <select
                  className="input"
                  value={durasi}
                  disabled={sibuk}
                  onChange={(e) => setDurasi(Number(e.target.value))}
                >
                  {PILIHAN_DURASI.map((p) => (
                    <option key={p.jam} value={p.jam}>{p.label}</option>
                  ))}
                </select>
              </label>

              <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
                {kode && !kedaluwarsa && (
                  <Btn variant="secondary" disabled={sibuk} onClick={tutup}>
                    {id ? "Tutup pendaftaran" : "Close registration"}
                  </Btn>
                )}
                <Btn variant="primary" disabled={sibuk} onClick={buka}>
                  {sibuk ? "…" : kode && !kedaluwarsa
                    ? (id ? "Kode baru" : "New code")
                    : (id ? "Buka pendaftaran" : "Open registration")}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Menghapus kelas — dengan dampaknya dipampang lebih dulu.
 *
 * Kelas kosong hanya perlu satu tekan. Kelas yang sudah berisi menuntut nama
 * persisnya diketik ulang, karena yang ikut terhapus bukan cuma barisnya:
 * peserta terdaftar, sesi, kehadiran, dan sertifikat yang sudah terbit semuanya
 * mengikut lewat cascade di basis data.
 */
function ModalHapusKelas({ kelas, programId, lang, onSelesai, onTutup }) {
  const id = lang === "id"
  const [dampak, setDampak] = React.useState(null)
  const [gagalMuat, setGagalMuat] = React.useState(false)
  const [ketikan, setKetikan] = React.useState("")
  const [sibuk, setSibuk] = React.useState(false)

  React.useEffect(() => {
    let batal = false
    classDeleteImpact(programId, kelas.id)
      .then((d) => { if (!batal) setDampak(d) })
      .catch((e) => {
        if (batal) return
        setGagalMuat(true)
        toast.error(e?.response?.data?.error || e.message)
      })
    return () => { batal = true }
  }, [programId, kelas.id])

  const kosong = dampak?.kosong
  const cocok = ketikan.trim() === kelas.name

  async function kirim() {
    setSibuk(true)
    try {
      await deleteClass(programId, kelas.id, kosong ? undefined : kelas.name)
      toast.success(id ? "Kelas dihapus" : "Class deleted")
      onSelesai()
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || e.message)
      setSibuk(false)
    }
  }

  const baris = dampak
    ? [
      [dampak.enrollments, id ? "peserta terdaftar" : "enrolled participants"],
      [dampak.sessions, id ? "sesi" : "sessions"],
      [dampak.attendances, id ? "catatan kehadiran" : "attendance records"],
      [dampak.certificates, id ? "sertifikat terbit" : "issued certificates"],
      [dampak.certBatches, id ? "batch sertifikat" : "certificate batches"],
      [dampak.certBatchRecipients, id ? "baris penerima batch" : "batch recipient rows"],
    ].filter(([n]) => n > 0)
    : []

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "Hapus kelas" : "Delete class"}</span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body col gap-3">
          <div style={{ fontWeight: 600 }}>{kelas.name}</div>

          {!dampak && !gagalMuat && (
            <div className="muted small">{id ? "Memeriksa isinya…" : "Checking contents…"}</div>
          )}

          {gagalMuat && (
            <div className="muted small">
              {id
                ? "Tidak bisa memeriksa isi kelas. Coba lagi nanti."
                : "Could not check the class contents. Try again later."}
            </div>
          )}

          {dampak && kosong && (
            <div className="muted small" style={{ lineHeight: 1.6 }}>
              {id
                ? "Kelas ini kosong — tidak ada peserta maupun sesi di dalamnya. Menghapusnya tidak menghilangkan apa pun yang lain."
                : "This class is empty — no participants, no sessions. Deleting it takes nothing else with it."}
            </div>
          )}

          {dampak && !kosong && (
            <>
              <div
                className="tile tile-pad col gap-1"
                style={{ lineHeight: 1.6, borderColor: "var(--danger, #b42318)" }}
              >
                <div className="small" style={{ fontWeight: 600 }}>
                  {id ? "Ikut terhapus permanen:" : "Permanently deleted along with it:"}
                </div>
                {baris.map(([n, label]) => (
                  <div key={label} className="small">• {n} {label}</div>
                ))}
              </div>
              <div className="tiny muted" style={{ lineHeight: 1.55 }}>
                {id
                  ? "Tidak bisa dibatalkan. Kalau maksudmu hanya menutup kelas yang sudah selesai, ubah statusnya jadi \"Selesai\" — riwayatnya tetap utuh."
                  : "This cannot be undone. If you only mean to close a finished class, set its status to \"Finished\" instead — the history stays intact."}
              </div>
              <label className="field">
                <span className="field-label">
                  {id ? `Ketik "${kelas.name}" untuk memastikan` : `Type "${kelas.name}" to confirm`}
                </span>
                <input
                  className="input"
                  value={ketikan}
                  onChange={(e) => setKetikan(e.target.value)}
                  placeholder={kelas.name}
                />
              </label>
            </>
          )}

          <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={onTutup} disabled={sibuk}>
              {id ? "Batal" : "Cancel"}
            </Btn>
            <Btn
              variant="danger"
              onClick={kirim}
              disabled={sibuk || !dampak || (!kosong && !cocok)}
            >
              {sibuk ? "…" : (id ? "Hapus kelas" : "Delete class")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Membuat sesi sekaligus dari rentang tanggal kelas.
 *
 * Sengaja TIDAK punya input tanggal: tanggalnya sudah ditetapkan saat kelas
 * dibuka, dan mengetiknya lagi di sini berarti dua sumber kebenaran yang bisa
 * berselisih. Yang ditanyakan hanya hal yang memang belum ada di kelas.
 */
function ModalGenerateSesi({ kelas, programId, lang, onSelesai, onTutup }) {
  const id = lang === "id"
  const [judul, setJudul] = React.useState(id ? "Sesi" : "Session")
  const [jam, setJam] = React.useState("09:00")
  const [frekuensi, setFrekuensi] = React.useState("WEEKLY")
  const [jamSelesai, setJamSelesai] = React.useState("")
  const [trainerId, setTrainerId] = React.useState("")
  const [trainers, setTrainers] = React.useState([])
  const [sibuk, setSibuk] = React.useState(false)

  React.useEffect(() => {
    if (!programId) return
    listProgramTrainers(programId).then(setTrainers).catch(() => setTrainers([]))
  }, [programId])

  const jumlah = jumlahSesi(kelas.start_date, kelas.end_date, frekuensi)
  const sudahAda = kelas._count?.sessions ?? 0
  const durasi = durasiDariJam(jam, jamSelesai)

  async function kirim() {
    setSibuk(true)
    try {
      const [j, m] = jam.split(":").map(Number)
      const mulai = new Date(kelas.start_date)
      mulai.setHours(j, m, 0, 0)

      const hasil = await createSession({
        programId,
        classId: kelas.id,
        title: judul.trim(),
        scheduledAt: mulai.toISOString(),
        trainerId: trainerId || undefined,
        durationMin: durasi.menit,
        // Server mewajibkan count >= 2; kelas sependek satu pertemuan dibuat
        // sebagai sesi tunggal biasa.
        ...(jumlah > 1
          ? { recurrence: { frequency: frekuensi, count: jumlah, numberTitles: true } }
          : {}),
      })

      const n = hasil?.seriesCount || 1
      toast.success(id ? `${n} sesi dibuat` : `${n} sessions created`)
      for (const w of hasil?.warnings || []) {
        toast(
          (w.type === "trainer"
            ? (id ? "Trainer bentrok dengan sesi: " : "Trainer conflicts with: ")
            : (id ? "Lokasi bentrok dengan sesi: " : "Location conflicts with: ")
          ) + w.sessionTitle,
          { icon: "⚠️", duration: 6000 }
        )
      }
      onSelesai()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setSibuk(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "Generate sesi" : "Generate sessions"}</span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body col gap-3">
          <div className="tile tile-pad" style={{ lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>{kelas.name}</div>
            <div className="tiny muted" style={{ marginTop: 3 }}>
              {tanggalRingkas(kelas.start_date, id)} – {tanggalRingkas(kelas.end_date, id)}
              {" → "}
              <strong>
                {jumlah} {id ? "sesi" : jumlah === 1 ? "session" : "sessions"}
                {jumlah > 1 && ` ${frekuensi === "DAILY" ? (id ? "harian" : "daily") : (id ? "mingguan" : "weekly")}`}
              </strong>
            </div>
          </div>

          {sudahAda > 0 && (
            <div className="tiny muted" style={{ lineHeight: 1.55 }}>
              {id
                ? `Kelas ini sudah punya ${sudahAda} sesi. Sesi baru ditambahkan, bukan menggantikan yang lama.`
                : `This class already has ${sudahAda} sessions. New ones are added, not replacing the existing.`}
            </div>
          )}

          <div className="row gap-2">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Judul dasar" : "Base title"}</span>
              <input className="input" value={judul} onChange={(e) => setJudul(e.target.value)} />
              <span className="tiny muted" style={{ marginTop: 4 }}>
                {jumlah > 1
                  ? (id
                    ? `Hasilnya "${judul.trim() || "Sesi"} 1", "${judul.trim() || "Sesi"} 2", …`
                    : `Becomes "${judul.trim() || "Session"} 1", "${judul.trim() || "Session"} 2", …`)
                  : (id ? "Dipakai apa adanya." : "Used as-is.")}
              </span>
            </label>
            <label className="field" style={{ width: 120 }}>
              <span className="field-label">{id ? "Jam mulai" : "Start time"}</span>
              <input className="input" type="time" value={jam} onChange={(e) => setJam(e.target.value)} />
            </label>
          </div>

          <div className="row gap-2">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Frekuensi" : "Frequency"}</span>
              <select className="input" value={frekuensi} onChange={(e) => setFrekuensi(e.target.value)}>
                <option value="WEEKLY">{id ? "Mingguan" : "Weekly"}</option>
                <option value="DAILY">{id ? "Harian" : "Daily"}</option>
              </select>
            </label>
            <label className="field" style={{ width: 160 }}>
              <span className="field-label">{id ? "Jam selesai (opsional)" : "End time (optional)"}</span>
              <input
                className="input"
                type="time"
                value={jamSelesai}
                onChange={(e) => setJamSelesai(e.target.value)}
              />
              <span
                className={durasi.valid ? "tiny muted" : "tiny"}
                style={{ marginTop: 4, ...(durasi.valid ? {} : { color: "var(--danger)" }) }}
              >
                {durasi.kosong
                  ? (id ? "Kosongkan bila belum pasti." : "Leave blank if not fixed yet.")
                  : durasi.valid
                    ? labelDurasi(durasi.menit, id)
                    : (id ? "Harus setelah jam mulai." : "Must be after the start time.")}
              </span>
            </label>
          </div>

          <label className="field">
            <span className="field-label">Trainer {id ? "(opsional)" : "(optional)"}</span>
            <select className="input" value={trainerId} onChange={(e) => setTrainerId(e.target.value)}>
              <option value="">{id ? "— saya sendiri —" : "— myself —"}</option>
              {trainers.map((tr) => (
                <option key={tr.user_id} value={tr.user_id}>
                  {tr.user?.full_name || tr.user?.email || tr.user_id}
                </option>
              ))}
            </select>
          </label>

          <Btn variant="primary" onClick={kirim} disabled={sibuk || !judul.trim() || !durasi.valid}>
            {sibuk ? "…" : (id ? `Buat ${jumlah} sesi` : `Create ${jumlah} ${jumlah === 1 ? "session" : "sessions"}`)}
          </Btn>
        </div>
      </div>
    </div>
  )
}

function ModalKelas({ kelas, lang, onSimpan, onTutup }) {
  const id = lang === "id"
  const baru = !kelas?.id
  const [nama, setNama] = React.useState(kelas?.name || "")
  const [kode, setKode] = React.useState(kelas?.code || "")
  const [mode, setMode] = React.useState(kelas?.mode || "PUBLIC")
  const [perusahaan, setPerusahaan] = React.useState(kelas?.client_company || "")
  const [mulai, setMulai] = React.useState(keInput(kelas?.start_date))
  const [selesai, setSelesai] = React.useState(keInput(kelas?.end_date))
  const [lokasi, setLokasi] = React.useState(kelas?.location || "")
  const [status, setStatus] = React.useState(kelas?.status || "DRAFT")
  const [sibuk, setSibuk] = React.useState(false)

  // Kelas inhouse tanpa nama perusahaan tidak punya arti — server menolaknya,
  // jadi tombolnya dimatikan di sini supaya tidak perlu bolak-balik.
  const kurang = !nama.trim() || (mode === "INHOUSE" && !perusahaan.trim())

  async function kirim() {
    setSibuk(true)
    await onSimpan({
      name: nama.trim(),
      code: kode.trim() || null,
      mode,
      client_company: mode === "INHOUSE" ? perusahaan.trim() : null,
      start_date: keISO(mulai),
      end_date: keISO(selesai),
      location: lokasi.trim() || null,
      status,
    })
    setSibuk(false)
  }

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">
            {baru ? (id ? "Buka kelas baru" : "New class") : (id ? "Ubah kelas" : "Edit class")}
          </span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body col gap-3">
          <label className="field">
            <span className="field-label">{id ? "Nama kelas" : "Class name"}</span>
            <input
              className="input"
              value={nama}
              placeholder={id ? "Kelas Feb 2026 — Pertamina" : "Feb 2026 — Pertamina"}
              onChange={(e) => setNama(e.target.value)}
            />
          </label>

          <div className="row gap-2">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Jenis" : "Mode"}</span>
              <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}>
                {MODE.map((m) => (
                  <option key={m} value={m}>
                    {m === "INHOUSE" ? "Inhouse" : (id ? "Publik" : "Public")}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Kode (opsional)" : "Code (optional)"}</span>
              <input className="input" value={kode} placeholder="ANGK-1" onChange={(e) => setKode(e.target.value)} />
            </label>
          </div>

          {mode === "INHOUSE" ? (
            <label className="field">
              <span className="field-label">{id ? "Perusahaan klien" : "Client company"}</span>
              <input
                className="input"
                value={perusahaan}
                placeholder="PT Pertamina"
                onChange={(e) => setPerusahaan(e.target.value)}
              />
              <span className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
                {id
                  ? "Seluruh peserta kelas ini mewarisi perusahaan tersebut."
                  : "Every participant in this class inherits this company."}
              </span>
            </label>
          ) : (
            <div className="tiny muted" style={{ lineHeight: 1.55 }}>
              {id
                ? "Kelas publik: perusahaan tiap peserta dicatat per orang saat pendaftaran."
                : "Public class: each participant's company is recorded per person at enrollment."}
            </div>
          )}

          <div className="row gap-2">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Mulai" : "Start"}</span>
              <input className="input" type="date" value={mulai} onChange={(e) => setMulai(e.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Selesai" : "End"}</span>
              <input className="input" type="date" value={selesai} onChange={(e) => setSelesai(e.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Status" : "Status"}</span>
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS.map((s) => <option key={s} value={s}>{labelStatus(s, id)}</option>)}
              </select>
            </label>
          </div>

          <label className="field">
            <span className="field-label">{id ? "Lokasi pelatihan (opsional)" : "Training location (optional)"}</span>
            <input
              className="input"
              value={lokasi}
              placeholder="Jakarta"
              onChange={(e) => setLokasi(e.target.value)}
            />
            <span className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
              {id
                ? "Dicetak di sertifikat sebagai tempat pelatihan."
                : "Printed on certificates as the training venue."}
            </span>
          </label>

          <Btn variant="primary" onClick={kirim} disabled={sibuk || kurang}>
            {sibuk ? "…" : (baru ? (id ? "Buka kelas" : "Create class") : (id ? "Simpan" : "Save"))}
          </Btn>
        </div>
      </div>
    </div>
  )
}
